/** Gmail / Google operational issues. */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const GOOGLE_ISSUES = {
  GMAIL_TOKEN_REVOKED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Gmail OAuth token has been revoked by the user",
    severity: "critical",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Gmail connection revoked",
    userGuidance:
      "Access to this Gmail account was revoked, so email can't be sent or received. Reconnect Gmail to restore it."
  },
  GMAIL_SCOPE_INSUFFICIENT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Gmail OAuth token is missing the send scope",
    severity: "high",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Gmail permissions out of date",
    userGuidance:
      "The Gmail connection is missing the permission needed to send email. Reconnect Gmail and grant send access."
  },
  GMAIL_QUOTA_EXCEEDED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Gmail API daily quota exceeded",
    severity: "high",
    // Google resets the sending quota at least daily, and the classifier also files its
    // per-minute rate limits here. A day with no failing send has crossed a reset.
    resolvesAfterQuietHours: 24,
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Gmail daily limit reached",
    userGuidance:
      "This Gmail account hit Google's daily sending limit. Sending resumes once the quota resets."
  },
  /**
   * `medium`, and never paging: the repair is the CUSTOMER's — reconnect the account — and they
   * are already told, twice, the moment the connection latches: the connection itself shows
   * NEEDS_REAUTH, and a `DEPLOYMENT_CONNECTION_ERROR` notification reaches the org (the
   * mailbox sweeps' `partitionAwaitingReauth` names both). Nothing an operator does clears it.
   * Resolved when the condition clears: by `OAuthTokenManager` on the next successful refresh,
   * and by `ReconcileTokenRefreshIssuesUseCase` for every connection no longer NEEDS_REAUTH —
   * reconnected, refreshed elsewhere, or deleted.
   */
  GOOGLE_TOKEN_REFRESH_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Google OAuth refresh token is invalid or expired",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Google connection expired",
    userGuidance:
      "Your Google sign-in expired and couldn't be refreshed. Reconnect the Google account to continue."
  },
  GMAIL_INVALID_THREAD_ID: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Gmail thread ID no longer exists",
    severity: "low"
  }
} as const satisfies Record<string, IssueDefinition>;
